import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { Search, ChevronRight, Menu, X, Home, ChevronLeft, PanelLeftClose, PanelLeftOpen, Sliders } from 'lucide-react';
import { CATEGORIES, ALL_TOOLS, Tool } from '@/src/constants/tools';
import Navbar from './Navbar';
import Footer from './Footer';
import FeedbackTab from '../common/FeedbackTab';

import AdSlot from './AdSlot';

export default function MainLayout({ children }: { children?: React.ReactNode }) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategoryId, setExpandedCategoryId] = useState<string | null>(null);
  const [isDataParsersOpen, setIsDataParsersOpen] = useState(false);
  const [expandedDataParserSubcat, setExpandedDataParserSubcat] = useState<string | null>(null);
  const location = useLocation();
  const currentTool = ALL_TOOLS.find(t => t.slug === location.pathname);

  const toggleCategory = (id: string) => {
    if (!isSidebarOpen) {
      setIsSidebarOpen(true);
      setExpandedCategoryId(id);
      return;
    }
    setExpandedCategoryId(prev => prev === id ? null : id);
  };

  const filteredTools = ALL_TOOLS.filter(t => 
    t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Auto-expand category based on current path
  useEffect(() => {
    if (currentTool) {
      const cat = CATEGORIES.find(c => c.tools.some(t => t.id === currentTool.id));
      if (cat) {
        if (cat.section === 'DATA_PARSERS') {
          setIsDataParsersOpen(true);
          setExpandedDataParserSubcat(cat.id);
        } else if (expandedCategoryId !== cat.id) {
          setExpandedCategoryId(cat.id);
        }
      }
    }
  }, [location.pathname]);

  // Force scroll restoration to top of viewport instantly on route updates
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.pathname]);

  // Handle custom toggle-sidebar event
  useEffect(() => {
    const handleToggle = (event: CustomEvent) => {
      const { categoryId } = event.detail;
      toggleCategory(categoryId);
    };
    window.addEventListener('toggle-sidebar', handleToggle as EventListener);
    return () => window.removeEventListener('toggle-sidebar', handleToggle as EventListener);
  }, [toggleCategory]);

  // Prevent dynamic main page background scrolling when mobile sidebar is active
  useEffect(() => {
    const handleBodyScroll = () => {
      const isMobile = window.matchMedia('(max-width: 1279px)').matches;
      if (isSidebarOpen && isMobile) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    };

    handleBodyScroll();

    const mediaQuery = window.matchMedia('(max-width: 1279px)');
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleBodyScroll);
    } else {
      mediaQuery.addListener(handleBodyScroll);
    }

    return () => {
      document.body.style.overflow = '';
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleBodyScroll);
      } else {
        mediaQuery.removeListener(handleBodyScroll);
      }
    };
  }, [isSidebarOpen]);

  const isHome = location.pathname === '/';

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col overflow-x-hidden max-w-[100vw]">
      <Navbar 
        activeTab={isHome ? 'dashboard' : ''} 
        setActiveTab={() => {}} 
        onMenuClick={() => setIsSidebarOpen(true)} 
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
      />
      
      <div className="flex flex-1 relative">
        {/* Mobile Sidebar Overlay (Backdrop) */}
        <AnimatePresence>
          {isSidebarOpen && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsSidebarOpen(false)}
              className="xl:hidden fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[45]"
            />
          )}
        </AnimatePresence>

        {/* Sidebar */}
        <aside className={`
          fixed top-0 left-0 h-screen z-50
          xl:sticky xl:top-[114px] xl:h-[calc(100vh-114px)] xl:z-40
          bg-white border-r border-slate-200 transition-all duration-300
          w-80 max-w-[300px]
          ${isSidebarOpen ? 'translate-x-0 xl:w-80' : '-translate-x-full xl:translate-x-0 xl:w-20'}
          overflow-hidden flex flex-col
        `}>
          <div className="p-4 border-b border-slate-100 flex items-center justify-between min-h-[73px]">
            {isSidebarOpen ? (
              <div className="flex items-center gap-2 flex-1">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                  <input 
                    type="text"
                    placeholder="Quick Search..."
                    className="w-full bg-slate-50 border-none rounded-lg pl-9 pr-4 py-2 text-xs font-bold focus:ring-2 focus:ring-brand placeholder:text-slate-400"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <button 
                  onClick={() => setIsSidebarOpen(false)}
                  className="xl:hidden p-2 hover:bg-slate-50 rounded-lg text-slate-400 transition-colors"
                  title="Close Menu"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <button 
                onClick={() => setIsSidebarOpen(true)} 
                className="mx-auto w-10 h-10 flex items-center justify-center hover:bg-slate-50 rounded-xl text-slate-400 transition-colors"
                title="Search Tools"
              >
                <Search size={18} />
              </button>
            )}
            <button 
              onClick={() => setIsSidebarOpen(!isSidebarOpen)} 
              className="hidden xl:flex items-center justify-center w-8 h-8 hover:bg-slate-50 rounded-lg text-slate-400 flex-shrink-0 transition-all active:scale-95"
              title={isSidebarOpen ? "Collapse Sidebar" : "Expand Sidebar"}
            >
              {isSidebarOpen ? <PanelLeftClose size={18} /> : <PanelLeftOpen size={18} />}
            </button>
          </div>

          <nav className="flex-1 overflow-y-auto p-4 custom-scrollbar text-sans pb-24 [-webkit-overflow-scrolling:touch]">
            {searchQuery ? (
              <div className="space-y-1">
                {filteredTools.map(tool => (
                  <SidebarItem key={tool.id} tool={tool} isActive={location.pathname === tool.slug} collapsed={!isSidebarOpen} onClick={() => setIsSidebarOpen(false)} />
                ))}
              </div>
            ) : (
              <div className="space-y-8 pb-10">
                {/* Finance Empire Section */}
                <div>
                   {isSidebarOpen && (
                    <div className="px-3 mb-4">
                      <span className="text-[10px] font-black text-slate-300 uppercase tracking-[0.2em] block py-1 border-b border-slate-50">FINANCE BLOCK</span>
                    </div>
                  )}
                  <div className="space-y-2 text-sans">
                    {CATEGORIES.filter(c => c.section === 'FINANCE').map(category => (
                      <div key={category.id} className="space-y-1">
                        <button 
                          onClick={() => toggleCategory(category.id)}
                          className={`
                            w-full flex items-center justify-between p-3 rounded-xl transition-all duration-200 group
                            ${expandedCategoryId === category.id 
                              ? 'bg-brand/5 text-brand ring-1 ring-brand/10' 
                              : 'text-slate-600 hover:bg-slate-50'}
                          `}
                        >
                          <div className="flex items-center gap-3">
                            <category.icon 
                              size={18} 
                              className={`transition-colors ${expandedCategoryId === category.id ? 'text-brand' : 'text-slate-400 group-hover:text-slate-600'}`} 
                            />
                            {isSidebarOpen && (
                              <span className={`text-sm font-bold tracking-tight ${expandedCategoryId === category.id ? 'text-brand' : ''}`}>
                                {category.name}
                              </span>
                            )}
                          </div>
                          {isSidebarOpen && (
                            <div className="flex items-center">
                              {expandedCategoryId === category.id ? (
                                <div 
                                  className="p-1 hover:bg-brand/10 rounded-md transition-colors"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleCategory(category.id);
                                  }}
                                >
                                  <X size={14} className="text-brand" />
                                </div>
                              ) : (
                                <ChevronRight 
                                  size={16} 
                                  className="text-slate-300 group-hover:text-slate-400 transition-transform" 
                                />
                              )}
                            </div>
                          )}
                        </button>
                        {isSidebarOpen && expandedCategoryId === category.id && (
                          <div className="ml-4 pl-4 border-l border-slate-100 space-y-1 py-1">
                            {category.tools.map(tool => (
                              <SidebarItem key={tool.id} tool={tool} isActive={location.pathname === tool.slug} collapsed={false} onClick={() => setIsSidebarOpen(false)} />
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Health Empire Section */}
                <div>
                  {isSidebarOpen && (
                    <div className="px-3 mb-4">
                      <span className="text-[10px] font-black text-slate-300 uppercase tracking-[0.2em] block py-1 border-b border-slate-50">HEALTH BLOCK</span>
                    </div>
                  )}
                  <div className="space-y-2 text-sans">
                    {CATEGORIES.filter(c => c.section === 'HEALTH').map(category => (
                      <div key={category.id} className="space-y-1">
                        <button 
                          onClick={() => toggleCategory(category.id)}
                          className={`
                            w-full flex items-center justify-between p-3 rounded-xl transition-all duration-200 group
                            ${expandedCategoryId === category.id 
                              ? 'bg-emerald-50 text-emerald-600 ring-1 ring-emerald-100' 
                              : 'text-slate-600 hover:bg-slate-50'}
                          `}
                        >
                          <div className="flex items-center gap-3">
                            <category.icon 
                              size={18} 
                              className={`transition-colors ${expandedCategoryId === category.id ? 'text-emerald-500' : 'text-slate-400 group-hover:text-slate-600'}`} 
                            />
                            {isSidebarOpen && (
                              <span className={`text-sm font-bold tracking-tight ${expandedCategoryId === category.id ? 'text-emerald-600' : ''}`}>
                                {category.name}
                              </span>
                            )}
                          </div>
                          {isSidebarOpen && (
                            <div className="flex items-center">
                              {expandedCategoryId === category.id ? (
                                <div 
                                  className="p-1 hover:bg-emerald-100 rounded-md transition-colors"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleCategory(category.id);
                                  }}
                                >
                                  <X size={14} className="text-emerald-500" />
                                </div>
                              ) : (
                                <ChevronRight 
                                  size={16} 
                                  className="text-slate-300 group-hover:text-slate-400 transition-transform" 
                                />
                              )}
                            </div>
                          )}
                        </button>
                        {isSidebarOpen && expandedCategoryId === category.id && (
                          <div className="ml-4 pl-4 border-l border-slate-100 space-y-1 py-1">
                            {category.tools.map(tool => (
                              <SidebarItem key={tool.id} tool={tool} isActive={location.pathname === tool.slug} collapsed={false} onClick={() => setIsSidebarOpen(false)} isHealth={true} />
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Developer Suite Section */}
                <div>
                  {isSidebarOpen && (
                    <div className="px-3 mb-4">
                      <span className="text-[10px] font-black text-slate-300 uppercase tracking-[0.2em] block py-1 border-b border-slate-50">GENERAL SEO UTILITIES</span>
                    </div>
                  )}
                  <div className="space-y-2 text-sans">
                    {CATEGORIES.filter(c => c.section === 'DEVELOPER').map(category => (
                      <div key={category.id} className="space-y-1">
                        <button 
                          onClick={() => toggleCategory(category.id)}
                          className={`
                            w-full flex items-center justify-between p-3 rounded-xl transition-all duration-200 group
                            ${expandedCategoryId === category.id 
                              ? 'bg-indigo-50 text-indigo-600 ring-1 ring-indigo-100' 
                              : 'text-slate-600 hover:bg-slate-50'}
                          `}
                        >
                          <div className="flex items-center gap-3">
                            <category.icon 
                              size={18} 
                              className={`transition-colors ${expandedCategoryId === category.id ? 'text-indigo-500' : 'text-slate-400 group-hover:text-slate-600'}`} 
                            />
                            {isSidebarOpen && (
                              <span className={`text-sm font-bold tracking-tight ${expandedCategoryId === category.id ? 'text-indigo-650' : ''}`}>
                                {category.name}
                              </span>
                            )}
                          </div>
                          {isSidebarOpen && (
                            <div className="flex items-center">
                              {expandedCategoryId === category.id ? (
                                <div 
                                  className="p-1 hover:bg-indigo-100 rounded-md transition-colors"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleCategory(category.id);
                                  }}
                                >
                                  <X size={14} className="text-indigo-500" />
                                </div>
                              ) : (
                                <ChevronRight 
                                  size={16} 
                                  className="text-slate-300 group-hover:text-slate-400 transition-transform" 
                                  />
                              )}
                            </div>
                          )}
                        </button>
                        {isSidebarOpen && expandedCategoryId === category.id && (
                          <div className="ml-4 pl-4 border-l border-slate-100 space-y-1 py-1">
                            {category.tools.map(tool => (
                              <SidebarItem key={tool.id} tool={tool} isActive={location.pathname === tool.slug} collapsed={false} onClick={() => setIsSidebarOpen(false)} isIndigo={true} />
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* PDF & Image Utilities Section */}
                <div>
                  {isSidebarOpen && (
                    <div className="px-3 mb-4">
                      <span className="text-[10px] font-black text-slate-300 uppercase tracking-[0.2em] block py-1 border-b border-slate-50">PDF & IMAGE BLOCK</span>
                    </div>
                  )}
                  <div className="space-y-2 text-sans">
                    {CATEGORIES.filter(c => c.section === 'PDF').map(category => (
                      <div key={category.id} className="space-y-1">
                        <button 
                          onClick={() => toggleCategory(category.id)}
                          className={`
                            w-full flex items-center justify-between p-3 rounded-xl transition-all duration-200 group
                            ${expandedCategoryId === category.id 
                              ? 'bg-rose-50 text-rose-600 ring-1 ring-rose-105' 
                              : 'text-slate-600 hover:bg-slate-50'}
                          `}
                        >
                          <div className="flex items-center gap-3">
                            <category.icon 
                              size={18} 
                              className={`transition-colors ${expandedCategoryId === category.id ? 'text-rose-500' : 'text-slate-400 group-hover:text-slate-600'}`} 
                            />
                            {isSidebarOpen && (
                              <span className={`text-sm font-bold tracking-tight ${expandedCategoryId === category.id ? 'text-rose-600' : ''}`}>
                                {category.name}
                              </span>
                            )}
                          </div>
                          {isSidebarOpen && (
                            <div className="flex items-center">
                              {expandedCategoryId === category.id ? (
                                <div 
                                  className="p-1 hover:bg-rose-100 rounded-md transition-colors"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleCategory(category.id);
                                  }}
                                >
                                  <X size={14} className="text-rose-500" />
                                </div>
                              ) : (
                                <ChevronRight 
                                  size={16} 
                                  className="text-slate-300 group-hover:text-slate-400 transition-transform" 
                                  />
                              )}
                            </div>
                          )}
                        </button>
                        {isSidebarOpen && expandedCategoryId === category.id && (
                          <div className="ml-4 pl-4 border-l border-slate-100 space-y-1 py-1">
                            {category.tools.map(tool => (
                              <SidebarItem key={tool.id} tool={tool} isActive={location.pathname === tool.slug} collapsed={false} onClick={() => setIsSidebarOpen(false)} isRose={true} />
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Student & Productivity Section */}
                <div>
                  {isSidebarOpen && (
                    <div className="px-3 mb-4">
                      <span className="text-[10px] font-black text-slate-300 uppercase tracking-[0.2em] block py-1 border-b border-slate-50">STUDENT & PRODUCTIVITY</span>
                    </div>
                  )}
                  <div className="space-y-2 text-sans">
                    {CATEGORIES.filter(c => c.section === 'STUDENT').map(category => (
                      <div key={category.id} className="space-y-1">
                        <button 
                          onClick={() => toggleCategory(category.id)}
                          className={`
                            w-full flex items-center justify-between p-3 rounded-xl transition-all duration-200 group
                            ${expandedCategoryId === category.id 
                              ? 'bg-amber-50 text-amber-600 ring-1 ring-amber-100' 
                              : 'text-slate-600 hover:bg-slate-50'}
                          `}
                        >
                          <div className="flex items-center gap-3">
                            <category.icon 
                              size={18} 
                              className={`transition-colors ${expandedCategoryId === category.id ? 'text-amber-500' : 'text-slate-400 group-hover:text-slate-600'}`} 
                            />
                            {isSidebarOpen && (
                              <span className={`text-sm font-bold tracking-tight ${expandedCategoryId === category.id ? 'text-amber-600' : ''}`}>
                                {category.name}
                              </span>
                            )}
                          </div>
                          {isSidebarOpen && (
                            <div className="flex items-center">
                              {expandedCategoryId === category.id ? (
                                <div 
                                  className="p-1 hover:bg-amber-100 rounded-md transition-colors"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleCategory(category.id);
                                  }}
                                >
                                  <X size={14} className="text-amber-500" />
                                </div>
                              ) : (
                                <ChevronRight 
                                  size={16} 
                                  className="text-slate-300 group-hover:text-slate-400 transition-transform" 
                                  />
                              )}
                            </div>
                          )}
                        </button>
                        {isSidebarOpen && expandedCategoryId === category.id && (
                          <div className="ml-4 pl-4 border-l border-slate-100 space-y-1 py-1">
                            {category.tools.map(tool => (
                              <SidebarItem key={tool.id} tool={tool} isActive={location.pathname === tool.slug} collapsed={false} onClick={() => setIsSidebarOpen(false)} isAmber={true} />
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Advanced Computation Labs Section */}
                <div>
                  {isSidebarOpen && (
                    <div className="px-3 mb-4">
                      <span className="text-[10px] font-black text-slate-300 dark:text-slate-600 uppercase tracking-[0.2em] block py-1 border-b border-slate-50 dark:border-slate-800">ADVANCED COMPUTATION LABS</span>
                    </div>
                  )}
                  <div className="space-y-2 text-sans">
                    {CATEGORIES.filter(c => c.section === 'COMPUTATION').map(category => (
                      <div key={category.id} className="space-y-1">
                        <button 
                          onClick={() => toggleCategory(category.id)}
                          className={`
                            w-full flex items-center justify-between p-3 rounded-xl transition-all duration-200 group
                            ${expandedCategoryId === category.id 
                              ? 'bg-emerald-50 dark:bg-[#1e1f20] text-emerald-600 dark:text-[#00e699] ring-1 ring-emerald-100 dark:ring-emerald-955' 
                              : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-[#1e1f20]/50'}
                          `}
                        >
                          <div className="flex items-center gap-3">
                            <category.icon 
                              size={18} 
                              className={`transition-colors ${expandedCategoryId === category.id ? 'text-emerald-500 dark:text-[#00e699]' : 'text-slate-400 group-hover:text-slate-600 dark:group-hover:text-[#e3e3e3]'}`} 
                            />
                            {isSidebarOpen && (
                              <span className={`text-sm font-bold tracking-tight ${expandedCategoryId === category.id ? 'text-emerald-650 dark:text-[#e3e3e3]' : ''}`}>
                                {category.name}
                              </span>
                            )}
                          </div>
                          {isSidebarOpen && (
                            <div className="flex items-center">
                              {expandedCategoryId === category.id ? (
                                <div 
                                  className="p-1 hover:bg-emerald-100 dark:hover:bg-emerald-950 rounded-md transition-colors"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleCategory(category.id);
                                  }}
                                >
                                  <X size={14} className="text-emerald-500 dark:text-[#00e699]" />
                                </div>
                              ) : (
                                <ChevronRight 
                                  size={16} 
                                  className="text-slate-300 group-hover:text-slate-400 dark:group-hover:text-[#00e699] transition-transform" 
                                  />
                              )}
                            </div>
                          )}
                        </button>
                        {isSidebarOpen && expandedCategoryId === category.id && (
                          <div className="ml-4 pl-4 border-l border-slate-100 dark:border-slate-800 space-y-1 py-1">
                            {category.tools.map(tool => (
                              <SidebarItem key={tool.id} tool={tool} isActive={location.pathname === tool.slug} collapsed={false} onClick={() => setIsSidebarOpen(false)} isHealth={true} />
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* SEO & Content Audit Section */}
                <div>
                  {isSidebarOpen && (
                    <div className="px-3 mb-4">
                      <span className="text-[10px] font-black text-slate-300 dark:text-slate-600 uppercase tracking-[0.2em] block py-1 border-b border-slate-50 dark:border-slate-800">SEO & CONTENT AUDIT</span>
                    </div>
                  )}
                  <div className="space-y-2 text-sans">
                    {CATEGORIES.filter(c => c.section === 'SEO').map(category => (
                      <div key={category.id} className="space-y-1">
                        <button 
                          onClick={() => toggleCategory(category.id)}
                          className={`
                            w-full flex items-center justify-between p-3 rounded-xl transition-all duration-200 group
                            ${expandedCategoryId === category.id 
                              ? 'bg-emerald-50 dark:bg-[#1e1f20] text-emerald-600 dark:text-[#00e699] ring-1 ring-emerald-100 dark:ring-emerald-955' 
                              : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-[#1e1f20]/50'}
                          `}
                        >
                          <div className="flex items-center gap-3">
                            <category.icon 
                              size={18} 
                              className={`transition-colors ${expandedCategoryId === category.id ? 'text-emerald-500 dark:text-[#00e699]' : 'text-slate-400 group-hover:text-slate-600 dark:group-hover:text-[#e3e3e3]'}`} 
                            />
                            {isSidebarOpen && (
                              <span className={`text-sm font-bold tracking-tight ${expandedCategoryId === category.id ? 'text-emerald-650 dark:text-[#e3e3e3]' : ''}`}>
                                {category.name}
                              </span>
                            )}
                          </div>
                          {isSidebarOpen && (
                            <div className="flex items-center">
                              {expandedCategoryId === category.id ? (
                                <div 
                                  className="p-1 hover:bg-emerald-100 dark:hover:bg-emerald-950 rounded-md transition-colors"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleCategory(category.id);
                                  }}
                                >
                                  <X size={14} className="text-emerald-500 dark:text-[#00e699]" />
                                </div>
                              ) : (
                                <ChevronRight 
                                  size={16} 
                                  className="text-slate-300 group-hover:text-slate-400 dark:group-hover:text-[#00e699] transition-transform" 
                                  />
                              )}
                            </div>
                          )}
                        </button>
                        {isSidebarOpen && expandedCategoryId === category.id && (
                          <div className="ml-4 pl-4 border-l border-slate-100 dark:border-slate-800 space-y-1 py-1">
                            {category.tools.map(tool => (
                              <SidebarItem key={tool.id} tool={tool} isActive={location.pathname === tool.slug} collapsed={false} onClick={() => setIsSidebarOpen(false)} isHealth={true} />
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* PRO-HUB TOOLS Section */}
                <div className="mt-6">
                  {isSidebarOpen && (
                    <div className="px-3 mb-4">
                      <span className="text-[10px] font-black text-slate-300 dark:text-slate-600 uppercase tracking-[0.2em] block py-1 border-b border-slate-50 dark:border-slate-800">PRO-HUB TOOLS</span>
                    </div>
                  )}
                  <div className="space-y-2 text-sans">
                    {CATEGORIES.filter(c => c.section === 'PRO_HUB').map(category => (
                      <div key={category.id} className="space-y-1">
                        <button 
                          onClick={() => toggleCategory(category.id)}
                          className={`
                            w-full flex items-center justify-between p-3 rounded-xl transition-all duration-200 group
                            ${expandedCategoryId === category.id 
                              ? 'bg-emerald-50 dark:bg-[#1e1f20] text-emerald-600 dark:text-[#00e699] ring-1 ring-emerald-100 dark:ring-emerald-955' 
                              : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-[#1e1f20]/50'}
                          `}
                        >
                          <div className="flex items-center gap-3">
                            <category.icon 
                              size={18} 
                              className={`transition-colors ${expandedCategoryId === category.id ? 'text-[#00e699]' : 'text-slate-400 group-hover:text-slate-600 dark:group-hover:text-[#e3e3e3]'}`} 
                            />
                            {isSidebarOpen && (
                              <span className={`text-sm font-bold tracking-tight ${expandedCategoryId === category.id ? 'text-emerald-650 dark:text-[#e3e3e3]' : ''}`}>
                                {category.name}
                              </span>
                            )}
                          </div>
                          {isSidebarOpen && (
                            <div className="flex items-center">
                              {expandedCategoryId === category.id ? (
                                <div 
                                  className="p-1 hover:bg-[#00e699]/15 rounded-md transition-colors"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleCategory(category.id);
                                  }}
                                >
                                  <X size={14} className="text-[#00e699]" />
                                </div>
                              ) : (
                                <ChevronRight 
                                  size={16} 
                                  className="text-slate-300 group-hover:text-slate-400 dark:group-hover:text-[#00e699] transition-transform" 
                                  />
                              )}
                            </div>
                          )}
                        </button>
                        {isSidebarOpen && expandedCategoryId === category.id && (
                          <div className="ml-4 pl-4 border-l border-slate-100 dark:border-slate-800 space-y-1 py-1">
                            {category.tools.map(tool => (
                              <SidebarItem key={tool.id} tool={tool} isActive={location.pathname === tool.slug} collapsed={false} onClick={() => setIsSidebarOpen(false)} isHealth={true} />
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* CRYPTOGRAPHY & SECURITY LABS Section */}
                <div className="mt-6">
                  {isSidebarOpen && (
                    <div className="px-3 mb-4">
                      <span className="text-[10px] font-black text-slate-300 dark:text-slate-600 uppercase tracking-[0.2em] block py-1 border-b border-slate-50 dark:border-slate-800">CRYPTOGRAPHY & SECURITY LABS</span>
                    </div>
                  )}
                  <div className="space-y-2 text-sans">
                    {CATEGORIES.filter(c => c.section === 'SECURITY_LABS').map(category => (
                      <div key={category.id} className="space-y-1">
                        <button 
                          onClick={() => toggleCategory(category.id)}
                          className={`
                            w-full flex items-center justify-between p-3 rounded-xl transition-all duration-200 group
                            ${expandedCategoryId === category.id 
                              ? 'bg-[#00e699]/15 dark:bg-[#1e1f20] text-[#00e699] ring-1 ring-[#00e699]/30 dark:ring-emerald-955' 
                              : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-[#1e1f20]/50'}
                          `}
                        >
                          <div className="flex items-center gap-3">
                            <category.icon 
                              size={18} 
                              className={`transition-colors ${expandedCategoryId === category.id ? 'text-[#00e699]' : 'text-slate-400 group-hover:text-slate-600 dark:group-hover:text-[#e3e3e3]'}`} 
                            />
                            {isSidebarOpen && (
                              <span className={`text-sm font-bold tracking-tight ${expandedCategoryId === category.id ? 'text-[#00e699] dark:text-[#e3e3e3]' : ''}`}>
                                {category.name}
                              </span>
                            )}
                          </div>
                          {isSidebarOpen && (
                            <div className="flex items-center">
                              {expandedCategoryId === category.id ? (
                                <div 
                                  className="p-1 hover:bg-[#00e699]/15 rounded-md transition-colors"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleCategory(category.id);
                                  }}
                                >
                                  <X size={14} className="text-[#00e699]" />
                                </div>
                              ) : (
                                <ChevronRight 
                                  size={16} 
                                  className="text-slate-300 group-hover:text-slate-400 dark:group-hover:text-[#00e699] transition-transform" 
                                  />
                              )}
                            </div>
                          )}
                        </button>
                        {isSidebarOpen && expandedCategoryId === category.id && (
                          <div className="ml-4 pl-4 border-l border-slate-100 dark:border-slate-800 space-y-1 py-1">
                            {category.tools.map(tool => (
                              <SidebarItem key={tool.id} tool={tool} isActive={location.pathname === tool.slug} collapsed={false} onClick={() => setIsSidebarOpen(false)} isSecurity={true} />
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* DATA PARSERS & STRUCTURAL WORKSPACES Section */}
                <div className="mt-6">
                  {isSidebarOpen && (
                    <div className="px-3 mb-4">
                      <span className="text-[10px] font-black text-slate-300 dark:text-slate-600 uppercase tracking-[0.2em] block py-1 border-b border-slate-50 dark:border-slate-800">DATA PARSERS & LABS</span>
                    </div>
                  )}
                  <div className="space-y-2 text-sans">
                    <div className="space-y-1">
                      <button 
                        onClick={() => {
                          if (!isSidebarOpen) {
                            setIsSidebarOpen(true);
                          }
                          setIsDataParsersOpen(prev => !prev);
                        }}
                        className={`
                          w-full flex items-center justify-between p-3 rounded-xl transition-all duration-200 group
                          ${isDataParsersOpen 
                            ? 'bg-emerald-50 dark:bg-[#1e1f20] text-[#00e699] ring-1 ring-[#00e699]/30 dark:ring-emerald-955' 
                            : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-[#1e1f20]/50'}
                        `}
                      >
                        <div className="flex items-center gap-3">
                          <Sliders 
                            size={18} 
                            className={`transition-colors ${isDataParsersOpen ? 'text-[#00e699]' : 'text-slate-400 group-hover:text-slate-600 dark:group-hover:text-[#e3e3e3]'}`} 
                          />
                          {isSidebarOpen && (
                            <span className={`text-sm font-bold tracking-tight ${isDataParsersOpen ? 'text-emerald-650 dark:text-[#e3e3e3]' : ''}`}>
                              Data Parsers & Labs
                            </span>
                          )}
                        </div>
                        {isSidebarOpen && (
                          <ChevronRight 
                            size={16} 
                            className={`text-slate-300 group-hover:text-slate-400 dark:group-hover:text-[#00e699] transition-transform duration-250 ${isDataParsersOpen ? 'rotate-90' : ''}`} 
                          />
                        )}
                      </button>

                      {/* Nested sub-accordions */}
                      {isSidebarOpen && isDataParsersOpen && (
                        <div className="ml-4 pl-3 border-l border-slate-100 dark:border-slate-800 space-y-2 py-2">
                          {CATEGORIES.filter(c => c.section === 'DATA_PARSERS').map(category => (
                            <div key={category.id} className="space-y-1">
                              <button
                                onClick={() => setExpandedDataParserSubcat(prev => prev === category.id ? null : category.id)}
                                className={`
                                  w-full flex items-center justify-between p-2 rounded-lg text-xs font-bold transition-all duration-150
                                  ${expandedDataParserSubcat === category.id
                                    ? 'bg-[#00e699]/15 dark:bg-[#1e1f20]/60 text-[#00e699]'
                                    : 'text-slate-500 dark:text-slate-400 hover:bg-slate-50/50 dark:hover:bg-[#1e1f20]/30'}
                                `}
                              >
                                <div className="flex items-center gap-2">
                                  <category.icon size={14} className={expandedDataParserSubcat === category.id ? 'text-[#00e699]' : 'text-slate-400'} />
                                  <span>{category.name}</span>
                                </div>
                                <ChevronRight 
                                  size={12} 
                                  className={`text-slate-300 transition-transform ${expandedDataParserSubcat === category.id ? 'rotate-90' : ''}`} 
                                />
                              </button>

                              {expandedDataParserSubcat === category.id && (
                                <div className="ml-3 pl-3 border-l border-slate-100/60 dark:border-slate-800/60 space-y-1 py-1">
                                  {category.tools.map(tool => (
                                    <SidebarItem 
                                      key={tool.id} 
                                      tool={tool} 
                                      isActive={location.pathname === tool.slug} 
                                      collapsed={false} 
                                      onClick={() => setIsSidebarOpen(false)} 
                                      isSecurity={true} 
                                    />
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 min-w-0 flex flex-col max-w-[100vw] overflow-x-hidden">
          <div className="flex-1 w-full max-w-[100vw] lg:max-w-screen-xl mx-auto overflow-x-hidden">
            {!isHome && (
              <div className="px-6 md:px-12 pt-8">
                <Breadcrumbs />
                <AdSlot id="TOP_AD" className="my-8" />
              </div>
            )}
             {children || <Outlet />}

             {currentTool && (
               <div className="mt-16 border-t border-slate-200/60 dark:border-slate-800/60 pt-16 pb-8 text-sans max-w-full lg:max-w-4xl mx-auto px-6">
                 <div className="bg-white dark:bg-[#18181B] border border-slate-200/80 dark:border-white/[0.08] rounded-3xl p-8 shadow-sm">
                   <h2 className="text-2xl font-black text-slate-900 dark:text-white uppercase tracking-tight mb-4">
                     {currentTool.name} — Usage Overview, Calculations & FAQ
                   </h2>
                   <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed mb-8">
                     The <strong className="text-brand dark:text-brand-light font-black">{currentTool.name}</strong> is a dedicated utility crafted to perform high-resolution conversions, visual planning, and automated diagnostic calculations. It operates dynamically on client-side threads to ensure total security and offline operation. Review the mathematical models and baseline system FAQs below.
                   </p>

                   <div className="grid md:grid-cols-2 gap-8">
                     <div>
                       <h3 className="text-xs font-black text-slate-400 uppercase tracking-[0.2em] mb-4">How to Use the Tool</h3>
                       <ol className="space-y-3 text-xs font-bold text-slate-500 dark:text-slate-400">
                         <li className="flex gap-2">
                           <span className="text-brand dark:text-emerald-400 font-mono">01.</span>
                           <span>Locate the primary interactive console area on this page and enter your parameters.</span>
                         </li>
                         <li className="flex gap-2">
                           <span className="text-brand dark:text-emerald-400 font-mono">02.</span>
                           <span>The algorithmic processor operates instantly in real-time as values are filled or adjusted.</span>
                         </li>
                         <li className="flex gap-2">
                           <span className="text-brand dark:text-emerald-400 font-mono">03.</span>
                           <span>Retrieve, copy, or print the comprehensive data sheets and charts directly from the dashboard.</span>
                         </li>
                       </ol>
                     </div>

                     <div>
                       <h3 className="text-xs font-black text-slate-400 uppercase tracking-[0.2em] mb-4">Core Standards & FAQ</h3>
                       <div className="space-y-4 text-xs text-slate-500 dark:text-slate-400">
                         <div>
                           <h4 className="font-bold text-slate-700 dark:text-slate-300">Are calculation metrics secure?</h4>
                           <p className="font-medium">Yes. Built under absolute privacy protocols, calculations are compiled completely locally within your active browser session. No data is stored, cached, or dispatched externally.</p>
                         </div>
                         <div>
                           <h4 className="font-bold text-slate-700 dark:text-slate-300">How is accuracy verified?</h4>
                           <p className="font-medium">Mathematical architectures on MetaUtilix are verified against official institutional code practices and industry standards (such as SF angles, decimal limits, and certified formula datasets).</p>
                         </div>
                       </div>
                     </div>
                   </div>
                 </div>
               </div>
             )}
           </div>
          <Footer />
        </main>
      </div>
      <FeedbackTab />
    </div>
  );
}

interface SidebarItemProps {
  tool: Tool;
  isActive: boolean;
  collapsed: boolean;
  onClick?: () => void;
  isHealth?: boolean;
  isIndigo?: boolean;
  isRose?: boolean;
  isAmber?: boolean;
  isSecurity?: boolean;
}

const SidebarItem: React.FC<SidebarItemProps> = ({ tool, isActive, collapsed, onClick, isHealth, isIndigo, isRose, isAmber, isSecurity }) => {
  const activeClass = isHealth 
    ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/20' 
    : isIndigo
      ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/20'
      : isRose
        ? 'bg-rose-600 text-white shadow-lg shadow-rose-600/20'
        : isAmber
          ? 'bg-amber-500 text-white shadow-lg shadow-amber-500/20'
          : isSecurity
            ? 'bg-[#00e699] text-slate-950 font-bold shadow-lg shadow-[#00e699]/30 border border-[#00e699]/40'
            : 'bg-brand text-white shadow-lg shadow-brand/20';

  return (
    <Link 
      to={tool.slug}
      onClick={onClick}
      className={`
        flex items-center gap-3 p-3 rounded-xl text-sm font-medium transition-all
        ${isActive 
          ? activeClass 
          : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900'}
      `}
    >
      <tool.icon size={18} className={isActive ? 'text-white' : 'text-slate-400'} />
      {!collapsed && <span>{tool.name}</span>}
    </Link>
  );
}

function Breadcrumbs() {
  const location = useLocation();
  const pathnames = location.pathname.split('/').filter(x => x);

  return (
    <nav className="w-full flex flex-wrap items-center gap-3 text-[10px] font-black uppercase tracking-widest text-slate-400">
      <Link to="/" className="flex items-center gap-1.5 hover:text-slate-900 transition-colors">
        <Home size={12} />
        Home
      </Link>
      {pathnames.map((name, index) => {
        const isLast = index === pathnames.length - 1;
        const url = `/${pathnames.slice(0, index + 1).join('/')}`;
        return (
          <React.Fragment key={url}>
            <ChevronRight size={10} className="text-slate-300" />
            {isLast ? (
              <span className="text-brand">{name.replace(/-/g, ' ')}</span>
            ) : (
              <Link to={url} className="hover:text-slate-900 transition-colors">
                {name.replace(/-/g, ' ')}
              </Link>
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
}
